import {
  Box,
  Heading,
  Text,
  Stack,
  Image,
  SimpleGrid,
  UnorderedList,
  ListItem,
  Flex,
  Spacer,
} from "@chakra-ui/react";
import caseStudies from "../Data/caseStudies.json";

export interface CaseStudyDetailsProps {
  id: string;
  mode?: "light" | "dark"; // allow parent to control theme styling
}

function renderInline(text: string) {
  const parts = text.split(
    /(\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+\]\([^)]+\))/g
  );

  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) return <strong key={i}>{part.slice(2, -2)}</strong>;
    if (part.startsWith("*") && part.endsWith("*")) return <em key={i}>{part.slice(1, -1)}</em>;
    if (part.startsWith("[") && part.includes("](") && part.endsWith(")")) {
      const label = part.slice(1, part.indexOf("]("));
      const url = part.slice(part.indexOf("](") + 2, -1);
      return (
        <a
          key={i}
          href={url}
          style={{ color: "#3182ce", textDecoration: "underline" }}
          target="_blank"
          rel="noopener noreferrer"
        >
          {label}
        </a>
      );
    }
    return part;
  });
}

function formatContent(text: string) {
  if (!text) return null;
  const paragraphs = text.split(/\n\s*\n/);

  return (
    <Stack spacing={3}>
      {paragraphs.map((para, idx) => {
        if (para.trim().startsWith("•")) {
          const bullets = para
            .split("\n")
            .map((line) => line.trim())
            .filter((line) => line.startsWith("•"))
            .map((line) => line.replace(/^•\s*/, ""));

          return (
            <UnorderedList key={idx} pl={5} spacing={1}>
              {bullets.map((b, i) => (
                <ListItem key={i}>
                  <Text as="span">{renderInline(b)}</Text>
                </ListItem>
              ))}
            </UnorderedList>
          );
        }
        return (
          <Text key={idx} whiteSpace="pre-wrap">
            {renderInline(para)}
          </Text>
        );
      })}
    </Stack>
  );
}

export const CaseStudyDetails: React.FC<CaseStudyDetailsProps> = ({
  id,
  mode,
}) => {
  const study = caseStudies.find((s) => s.id === id);
  if (!study) return <Text>Case study not found.</Text>;

  const { title, subtitle, detailedContent, tenantLogoFile } = study;
  const { objective, challenge, solution, results, screenshots } = detailedContent;

  const bg = mode === "dark" ? "gray.800" : "white";
  const color = mode === "dark" ? "gray.100" : "gray.800";

  return (
    <Box p={6} bg={bg} color={color} borderRadius="lg" boxShadow="sm">
      <Flex align="center" mb={4}>
        <Box>
          <Heading mb={2}>{title}</Heading>
          {subtitle && (
            <Text fontStyle="italic" fontSize="lg" color="gray.500">
              {subtitle}
            </Text>
          )}
        </Box>
        <Spacer />
        {tenantLogoFile && (
          <Image
            src={tenantLogoFile}
            alt={`${title} logo`}
            maxH="60px"
            objectFit="contain"
          />
        )}
      </Flex>

      <Stack spacing={6}>
        <Box>
          <Heading size="sm" mb={2}>Objective</Heading>
          {formatContent(objective)}
        </Box>
        <Box>
          <Heading size="sm" mb={2}>Challenge</Heading>
          {formatContent(challenge)}
        </Box>
        <Box>
          <Heading size="sm" mb={2}>Solution</Heading>
          {formatContent(solution)}
        </Box>
        <Box>
          <Heading size="sm" mb={2}>Results</Heading>
          {formatContent(results)}
        </Box>
      </Stack>

      {screenshots && screenshots.length > 0 && (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt={6}>
          {screenshots.map((src, idx) => (
            <Image
              key={idx}
              src={src}
              alt={`${title} screenshot ${idx + 1}`}
              borderRadius="md"
              boxShadow="md"
            />
          ))}
        </SimpleGrid>
      )}
    </Box>
  );
};
